import { useSyncAllStores } from '@/features/Stores/hooks/useSyncAllStores';
import { toastError } from '@app/components/Toastify';
import ReactRootProviders from '@app/contexts/providers/ReactRootProviders';
import { UserSessionProvider, useUserSession } from '@app/contexts/providers/UserSessionProvider';
import { useConnectedStoresQuery } from '@app/hooks/queries/useConnectedStoresQuery';
import { Skeleton } from 'easyship-components';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { angularRouteTo } from '@app/utils/angular';
import { RouteToDestinations } from '@app/types/angular';
import ActionCard, { ActionCardType } from './components/ActionCard';
import { alertDisconnectedStores, handleStoreConnectionError, isStoreConnected } from './helpers';

function EmptyStateInner() {
  const { formatMessage } = useIntl();
  const { user } = useUserSession();
  const {
    data: stores,
    isFetched: isStoresFetched,
    isLoading: isLoadingStores,
  } = useConnectedStoresQuery();
  const { syncAllStores, syncSingleStore, isSyncProcessing } = useSyncAllStores();
  const isUserCanSyncOrders = useMemo(() => user.role.actions['shipments.syncOrders'], [user]);

  const onSyncOrdersClick = async () => {
    if (!isStoresFetched || !Array.isArray(stores) || isSyncProcessing) {
      return;
    }

    if (!isUserCanSyncOrders) {
      const id = 'reports.cant-sync-orders';
      toastError(formatMessage({ id }), { toastId: id });

      return;
    }

    if (stores.length === 0) {
      angularRouteTo(RouteToDestinations.Connect);
      return;
    }

    const isSingleStore = stores.length === 1;

    if (isSingleStore) {
      const store = stores[0];

      if (!isStoreConnected(store)) {
        handleStoreConnectionError(
          store,
          formatMessage({ id: 'reports.store-connection-error' }, { store: store.name }),
          formatMessage({ id: 'global.fix-now' })
        );

        alertDisconnectedStores([store]);
        return;
      }

      await syncSingleStore();
      return;
    }

    const disconnectedStores = stores.filter((store) => !isStoreConnected(store));

    if (disconnectedStores.length > 0) {
      const id = 'reports.stores-connection-error';
      toastError(formatMessage({ id }), {
        toastId: id,
      });

      alertDisconnectedStores(disconnectedStores);
    }

    await syncAllStores();
  };

  const onManualInputClick = useCallback(() => {
    angularRouteTo(RouteToDestinations.ManualInput);
  }, []);

  const onImportFileClick = useCallback(() => {
    angularRouteTo(RouteToDestinations.FileUpload);
  }, []);

  return (
    <div className="text-center px-4 py-[76px]">
      <p className="mb-16 text-xl text-ink-900">
        <FormattedMessage id="shipments.list.empty.advanced.no-shipments" />
      </p>
      <div className="flex gap-8 h-[338px]">
        {isLoadingStores ? (
          <Skeleton className="flex-1 h-full rounded-xl border-none" />
        ) : (
          <ActionCard
            isWorking={isSyncProcessing}
            type={ActionCardType.SyncOrders}
            storeCount={stores?.length || 0}
            store={stores?.length === 1 ? stores[0] : undefined}
            onClick={onSyncOrdersClick}
          />
        )}

        <ActionCard
          type={ActionCardType.ManualInput}
          onClick={onManualInputClick}
          isWorking={isSyncProcessing}
        />

        <ActionCard
          type={ActionCardType.ImportFile}
          onClick={onImportFileClick}
          isWorking={isSyncProcessing}
        />
      </div>
    </div>
  );
}

export function AdvancedEmptyState() {
  return (
    <ReactRootProviders>
      <UserSessionProvider>
        <EmptyStateInner />
      </UserSessionProvider>
    </ReactRootProviders>
  );
}

export default AdvancedEmptyState;
